import { useEffect, useMemo, useState, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDarkMode } from "../../../../../context/ThemeContext";
import {
  createGeofence,
  updateGeofence,
  deleteGeofence,
  type Geofence,
} from "../../../../../store/admin/geofencesStore";
import { useGeofencesList } from "../../../../../features/geofences/queries";
import { qk } from "../../../../../lib/queryKeys";
import { useCompaniesStore } from "../../../../../store/admin/companiesStore";
import { useAlert } from "../../../../../context/AlertContext";
import { resetLastProcessedCoordinates } from "../../../../../components/base/MapPicker";
import type { TFunction } from "i18next";

import { useGeofenceForm } from "./hooks/useGeofenceForm";
import { GeofenceHeader } from "./components/GeofenceHeader";
import { GeofenceList } from "./components/GeofenceList";
import { GeofencePagination } from "./components/GeofencePagination";
import { GeofenceModal } from "./components/GeofenceModal";
import { GeofenceViewerModal } from "./components/GeofenceViewerModal";
import type { ThemeClasses } from "./types";

// Estilos CSS para scrollbars personalizados
const scrollbarStyles = `
  .scrollbar-light::-webkit-scrollbar {
    width: 6px;
  }
  .scrollbar-light::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  .scrollbar-light::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  .scrollbar-light::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
  
  .scrollbar-dark::-webkit-scrollbar {
    width: 6px;
  }
  .scrollbar-dark::-webkit-scrollbar-track {
    background: #374151;
    border-radius: 3px;
  }
  .scrollbar-dark::-webkit-scrollbar-thumb {
    background: #6b7280;
    border-radius: 3px;
  }
  .scrollbar-dark::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
`;

export default function GeofencesSection() {
  const { isDarkMode } = useDarkMode();
  const { showAlert } = useAlert();

  // Cleanup scroll lock on unmount
  useEffect(() => {
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  // Estados de filtros
  const [selectedType, setSelectedType] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedShape, setSelectedShape] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Estados de UI
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedView, setSelectedView] = useState<"grid" | "list">("list");
  const [loadingSubmit, setLoadingSubmit] = useState(false);

  // Estados de edición
  const [isEdit, setIsEdit] = useState(false);
  const [editGeofence, setEditGeofence] = useState<Geofence | null>(null);

  // Estados del visor de mapa
  const [showViewerModal, setShowViewerModal] = useState(false);
  const [viewerGeofence, setViewerGeofence] = useState<Geofence | null>(null);

  // Paginación
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const filterParams = useMemo(
    () => ({
      page: currentPage,
      limit: itemsPerPage,
      shape: selectedShape !== "all" ? selectedShape : undefined,
      type: selectedType !== "all" ? selectedType : undefined,
      status: selectedStatus !== "all" ? selectedStatus : undefined,
      search: searchQuery,
    }),
    [
      currentPage,
      itemsPerPage,
      selectedShape,
      selectedType,
      selectedStatus,
      searchQuery,
    ],
  );

  const geofencesQuery = useGeofencesList(filterParams);
  const geofences = geofencesQuery.data?.data ?? [];
  const meta = geofencesQuery.data?.meta;
  const loading = geofencesQuery.isLoading;
  const totalPages = meta?.totalPages || 1;

  const queryClient = useQueryClient();
  const companyId = useCompaniesStore((s) => s.companyId);
  const invalidateGeofences = () => {
    queryClient.invalidateQueries({ queryKey: ["geofences"] });
    queryClient.invalidateQueries({ queryKey: qk.counts(companyId) });
  };

  // Custom hook para el formulario
  const {
    form,
    shape,
    polygonPoints,
    setPolygonPoints,
    linearPoints,
    setLinearPoints,
    routeDistance,
    estimatedTime,
    polygonArea,
    hasRealChanges,
    handleAddressSelect,
    handleLinearPointsChange,
    handleAxisCalculated,
    handleBufferChange,
    linearAddresses,
    handleLinearAddressEdit,
    handleRouteCalculated,
    resetForm,
    prepareGeofenceData,
    defaultCircularCoordinates,
  } = useGeofenceForm({
    isEdit,
    editGeofence,
  });

  // Theme classes
  const themeClasses: ThemeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
  };

  // Translation mock
  const t: TFunction<"translation", undefined> = ((key: string) =>
    key) as TFunction<"translation", undefined>;

  // Handlers de filtros
  const handleTypeChange = (value: string) => {
    setSelectedType(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
    setCurrentPage(1);
  };

  const handleShapeChange = (value: string) => {
    setSelectedShape(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearchQuery(e.target.value);
  };

  // Handlers de vista
  const handleViewChange = (view: "grid" | "list") => {
    setSelectedView(view);
  };

  // Handlers de modal
  const handleAddNew = () => {
    setShowAddModal(true);
    // Disable background scroll
    document.body.style.overflow = "hidden";
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setIsEdit(false);
    setEditGeofence(null);
    setLoadingSubmit(false);
    resetForm();
    resetLastProcessedCoordinates();
    // Enable background scroll
    document.body.style.overflow = "";
  };

  // Handlers de acciones
  const handleEdit = useCallback((geofence: Geofence) => {
    setEditGeofence(geofence);
    setIsEdit(true);
    setTimeout(() => {
      setShowAddModal(true);
      // Disable background scroll
      document.body.style.overflow = "hidden";
    }, 0);
  }, []);

  const handleViewMap = useCallback((geofence: Geofence) => {
    setViewerGeofence(geofence);
    setShowViewerModal(true);
    // Disable background scroll
    document.body.style.overflow = "hidden";
  }, []);

  const handleCloseViewerModal = useCallback(() => {
    setShowViewerModal(false);
    setViewerGeofence(null);
    // Enable background scroll
    document.body.style.overflow = "";
  }, []);

  // Submit handlers
  const handleCreateGeofenceSubmit = async (data: any) => {
    setLoadingSubmit(true);
    const geofence = prepareGeofenceData(data);

    await createGeofence(
      geofence,
      showAlert,
      setLoadingSubmit,
      t,
      () => {
        setShowAddModal(false);
        form.reset();
      },
      form.reset,
    );

    invalidateGeofences();
  };

  const handleUpdateGeofenceSubmit = async (data: any) => {
    if (!editGeofence) return;
    setLoadingSubmit(true);

    const geofence = prepareGeofenceData(data);

    await updateGeofence(
      editGeofence.id,
      geofence,
      showAlert,
      setLoadingSubmit,
      t,
      () => {
        setShowAddModal(false);
        setIsEdit(false);
        setEditGeofence(null);
        form.reset();
      },
      form.reset,
    );

    invalidateGeofences();
  };

  const handleDeleteGeofence = (geofence: Geofence) => {
    showAlert({
      title: t("Eliminar geocerca"),
      text: t("¿Seguro que deseas eliminar esta geocerca?"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("Eliminar"),
      cancelButtonText: t("Cancelar"),
      confirmButtonColor: "#ef4444",
      onConfirm: async () => {
        setLoadingSubmit(true);
        await deleteGeofence(geofence.id, showAlert, setLoadingSubmit, t, () => {});
        invalidateGeofences();
      },
    });
  };

  const handleSubmit = isEdit
    ? handleUpdateGeofenceSubmit
    : handleCreateGeofenceSubmit;

  // Efecto para forzar forma al abrir modal en modo creación
  useEffect(() => {
    if (showAddModal && !isEdit) {
      form.setValue("shape", "circular");
    }
  }, [showAddModal, isEdit, form]);

  return (
    <>
      <style>{scrollbarStyles}</style>
      <div className="h-full flex flex-col">
        {/* Header */}
        <GeofenceHeader
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          selectedShape={selectedShape}
          selectedType={selectedType}
          selectedStatus={selectedStatus}
          searchQuery={searchQuery}
          selectedView={selectedView}
          onShapeChange={handleShapeChange}
          onTypeChange={handleTypeChange}
          onStatusChange={handleStatusChange}
          onSearchChange={handleSearchChange}
          onViewChange={handleViewChange}
          onAddNew={handleAddNew}
        />

        {/* Content */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden min-w-0 p-3 sm:p-6">
          <GeofenceList
            geofences={geofences}
            loading={loading}
            selectedView={selectedView}
            themeClasses={themeClasses}
            isDarkMode={isDarkMode}
            onEdit={handleEdit}
            onViewMap={handleViewMap}
            onDelete={handleDeleteGeofence}
          />

          {/* Paginación */}
          {geofences.length > 0 && (
            <GeofencePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>

        {/* Modal de Creación/Edición */}
        <GeofenceModal
          isOpen={showAddModal}
          isEdit={isEdit}
          loadingSubmit={loadingSubmit}
          form={form}
          shape={shape}
          themeClasses={themeClasses}
          isDarkMode={isDarkMode}
          editGeofence={editGeofence}
          polygonPoints={polygonPoints}
          setPolygonPoints={setPolygonPoints}
          linearPoints={linearPoints}
          setLinearPoints={setLinearPoints}
          routeDistance={routeDistance}
          estimatedTime={estimatedTime}
          polygonArea={polygonArea}
          defaultCircularCoordinates={defaultCircularCoordinates}
          hasRealChanges={hasRealChanges}
          onAddressSelect={handleAddressSelect}
          onLinearPointsChange={handleLinearPointsChange}
          onRouteCalculated={handleRouteCalculated}
          onAxisCalculated={handleAxisCalculated}
          onBufferChange={handleBufferChange}
          linearAddresses={linearAddresses}
          onLinearAddressEdit={handleLinearAddressEdit}
          onSubmit={handleSubmit}
          onClose={handleCloseModal}
        />

        {/* Modal de Visualización */}
        <GeofenceViewerModal
          isOpen={showViewerModal}
          geofences={geofences}
          selectedGeofence={viewerGeofence}
          isDarkMode={isDarkMode}
          onClose={handleCloseViewerModal}
        />
      </div>
    </>
  );
}
